import CustomPagination from '@/platform/common/components/CustomPagination/CustomPagination';
import NoDataRow from '@/platform/common/components/NoDataRow/NoDataRow';
import SearchBarFilter from '@/platform/common/components/SearchBarFilter/SearchBarFilter';
import SelectedCustomer from '@/platform/common/components/SelectedCustomer/SelectedCustomer';
import { useFilters } from '@/platform/hooks/useFilters';
import { localDateToMinutes } from '@/platform/lib/utils';
import { useConfirmationStore } from '@/platform/stores/useConfirmationStore';
import {
    Badge,
    Button,
    Container,
    FilterBarToggle,
    Menu,
    MenuButton,
    MenuItem,
    MenuItems,
    MenuSeparator,
    Segment,
    SegmentContent,
    SegmentFooter,
    SegmentHeader,
    Table,
    TableBody,
    TableCell,
    TableHeader,
    TableHeaderCell,
    TableRow,
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from '@/platform/ui';
import {
    ArrowPathIcon,
    EllipsisVerticalIcon,
    ExclamationTriangleIcon,
    LinkIcon,
    PencilIcon,
    PlusIcon,
    TrashIcon,
    UserMinusIcon,
} from '@heroicons/react/24/outline';
import { Link, router, usePage } from '@inertiajs/react';

const CustomerIndexView = () => {
    const { auth, entities } = usePage().props;
    const { filters, updateFilters } = useFilters(['entities']);
    const { confirm, setLoading } = useConfirmationStore();

    const deleteEntity = entity =>
        confirm({
            title: 'Entiteit verwijderen?',
            description: `Weet je zeker dat je ${entity.name} wilt verwijderen? Dit kun je niet ongedaan maken.`,
            confirmLabel: 'Verwijderen',
            onConfirm: close => {
                setLoading(true);
                router.delete(
                    `/customers/${auth?.user?.selected_customer_id}/entities/${entity.id}`,
                    {
                        preserveState: true,
                        preserveScroll: true,
                        onFinish: close,
                    }
                );
            },
        });

    const deleteEmptyEmployees = entity =>
        confirm({
            title: 'Lege werknemers verwijderen?',
            description: `Werknemers van ${entity.name} zonder documenten en zonder ontvangen doelgroepverklaring worden verwijderd. Dit kun je niet ongedaan maken.`,
            onConfirm: close => {
                setLoading(true);
                router.delete(
                    `/customers/${auth?.user?.selected_customer_id}/entities/${entity.id}/employees/empty`,
                    {
                        preserveState: true,
                        preserveScroll: true,
                        onFinish: close,
                    }
                );
            },
        });

    const deleteExitedEmployees = entity =>
        confirm({
            title: 'Uit dienst getreden werknemers verwijderen?',
            description: `Werknemers van ${entity.name} die meer dan 5 jaar geleden uit dienst zijn gegaan worden verwijderd. Dit kun je niet ongedaan maken.`,
            onConfirm: close => {
                setLoading(true);
                router.delete(
                    `/customers/${auth?.user?.selected_customer_id}/entities/${entity.id}/employees/exited`,
                    {
                        preserveState: true,
                        preserveScroll: true,
                        onFinish: close,
                    }
                );
            },
        });

    return (
        <Container>
            <SelectedCustomer />

            <Segment>
                <SegmentHeader
                    header={`Entiteiten (${entities?.meta?.total || 0})`}
                >
                    {auth?.permissions?.includes('create entities') && (
                        <Button
                            as={Link}
                            variant='primary'
                            href={`/customers/${auth?.user?.selected_customer_id}/entities/create`}
                        >
                            <PlusIcon className='size-5' />
                            Nieuwe entiteit
                        </Button>
                    )}
                </SegmentHeader>
                <SegmentContent>
                    <SearchBarFilter only={['entities']}>
                        <FilterBarToggle
                            label='Toon inactief'
                            checked={!!filters?.inactive}
                            onChange={checked =>
                                updateFilters('inactive', checked ? 1 : 0)
                            }
                        />
                    </SearchBarFilter>

                    <Table striped>
                        <TableHeader>
                            <TableRow>
                                <TableHeaderCell className='w-px'>
                                    Nr.
                                </TableHeaderCell>
                                <TableHeaderCell>
                                    Loonheffingennummer
                                </TableHeaderCell>
                                <TableHeaderCell>KvK-nummer</TableHeaderCell>
                                <TableHeaderCell>Naam</TableHeaderCell>
                                <TableHeaderCell textAlign='center'>
                                    API
                                </TableHeaderCell>
                                <TableHeaderCell textAlign='center'>
                                    Datahub
                                </TableHeaderCell>
                                <TableHeaderCell className='w-px'>
                                    <span className='sr-only'>Acties</span>
                                </TableHeaderCell>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {entities?.meta?.total === 0 && (
                                <NoDataRow numberOfColumns={7} />
                            )}

                            {entities?.data?.map(entity => (
                                <TableRow key={entity.id}>
                                    <TableCell className='w-px'>
                                        {entity.number}
                                    </TableCell>
                                    <TableCell singleLine>
                                        <span className='inline-flex items-center gap-2'>
                                            {entity.loonheffingennummer}
                                            {!entity.is_active && (
                                                <Badge color='red'>
                                                    Inactief
                                                </Badge>
                                            )}
                                        </span>
                                    </TableCell>
                                    <TableCell singleLine>
                                        {entity.kvk}
                                    </TableCell>
                                    <TableCell>
                                        {auth?.permissions?.includes(
                                            'update entities'
                                        ) ? (
                                            <Link
                                                href={`/customers/${auth?.user?.selected_customer_id}/entities/${entity.id}/edit`}
                                            >
                                                {entity.name}
                                            </Link>
                                        ) : (
                                            entity.name
                                        )}
                                    </TableCell>
                                    <TableCell textAlign='center'>
                                        {entity.api && (
                                            <Tooltip>
                                                <TooltipTrigger
                                                    aria-label={`API: ${entity.api}`}
                                                >
                                                    <LinkIcon className='size-5 text-gray-500' />
                                                </TooltipTrigger>
                                                <TooltipContent>
                                                    {entity.api}
                                                </TooltipContent>
                                            </Tooltip>
                                        )}
                                    </TableCell>
                                    <TableCell textAlign='center'>
                                        {entity.datahub_id && (
                                            <Tooltip>
                                                <TooltipTrigger
                                                    aria-label={
                                                        entity.last_datahub_sync
                                                            ? 'Laatste Datahub-synchronisatie'
                                                            : 'Laatste Datahub-synchronisatie onbekend'
                                                    }
                                                >
                                                    {entity.last_datahub_sync ? (
                                                        <ArrowPathIcon className='size-5 text-gray-500' />
                                                    ) : (
                                                        <ExclamationTriangleIcon className='size-5 text-orange-500' />
                                                    )}
                                                </TooltipTrigger>
                                                <TooltipContent>
                                                    {entity.last_datahub_sync
                                                        ? localDateToMinutes(
                                                              entity.last_datahub_sync
                                                          )
                                                        : 'Kon de laatste synchronisatiedatum niet ophalen'}
                                                </TooltipContent>
                                            </Tooltip>
                                        )}
                                    </TableCell>
                                    <TableCell
                                        className='w-px'
                                        singleLine
                                        textAlign='right'
                                    >
                                        {[
                                            'update entities',
                                            'delete entities',
                                            'destroy empty employees',
                                            'destroy exited employees',
                                        ]?.some(perm =>
                                            auth?.permissions?.includes(perm)
                                        ) && (
                                            <Menu>
                                                <MenuButton
                                                    aria-label={`Acties voor ${entity.name}`}
                                                >
                                                    <EllipsisVerticalIcon className='size-5' />
                                                </MenuButton>
                                                <MenuItems anchor='bottom end'>
                                                    {auth?.permissions?.includes(
                                                        'update entities'
                                                    ) && (
                                                        <MenuItem
                                                            as={Link}
                                                            href={`/customers/${auth?.user?.selected_customer_id}/entities/${entity.id}/edit`}
                                                            icon={PencilIcon}
                                                        >
                                                            Wijzigen
                                                        </MenuItem>
                                                    )}
                                                    {[
                                                        'delete entities',
                                                        'destroy empty employees',
                                                        'destroy exited employees',
                                                    ].some(perm =>
                                                        auth?.permissions?.includes(
                                                            perm
                                                        )
                                                    ) && <MenuSeparator />}
                                                    {auth?.permissions?.includes(
                                                        'destroy empty employees'
                                                    ) && (
                                                        <MenuItem
                                                            className='text-red-600'
                                                            icon={UserMinusIcon}
                                                            onClick={() =>
                                                                deleteEmptyEmployees(
                                                                    entity
                                                                )
                                                            }
                                                        >
                                                            Lege werknemers
                                                            verwijderen
                                                        </MenuItem>
                                                    )}
                                                    {auth?.permissions?.includes(
                                                        'destroy exited employees'
                                                    ) && (
                                                        <MenuItem
                                                            className='text-red-600'
                                                            icon={UserMinusIcon}
                                                            onClick={() =>
                                                                deleteExitedEmployees(
                                                                    entity
                                                                )
                                                            }
                                                        >
                                                            Werknemers langer
                                                            dan 5 jaar uit
                                                            dienst verwijderen
                                                        </MenuItem>
                                                    )}
                                                    {auth?.permissions?.includes(
                                                        'delete entities'
                                                    ) && (
                                                        <MenuItem
                                                            className='text-red-600'
                                                            icon={TrashIcon}
                                                            onClick={() =>
                                                                deleteEntity(
                                                                    entity
                                                                )
                                                            }
                                                        >
                                                            Entiteit verwijderen
                                                        </MenuItem>
                                                    )}
                                                </MenuItems>
                                            </Menu>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </SegmentContent>
                <SegmentFooter>
                    <CustomPagination
                        meta={entities?.meta}
                        only={['entities']}
                    />
                </SegmentFooter>
            </Segment>
        </Container>
    );
};

export default CustomerIndexView;
